import React, { useMemo, useState } from 'react';
import { Button, Card, Stack, Flex, useTheme, spacing, fontFamily } from '@fti/design-system';
import type { Session } from './types';
import { QuoteCard } from './QuoteCard';
import {
  copyToClipboard,
  downloadAsFile,
  formatQuotesForClipboard,
  quotesToCsv,
  quotesToTxt,
} from './exportUtils';

export interface ResultsPanelProps {
  session: Session;
  onToggleStar: (id: string) => void;
  onSetEdited: (id: string, text: string) => void;
  onResetEdit: (id: string) => void;
  onRetry: () => void;
  title: string;
}

function safeFilename(title: string, ext: string): string {
  const base = title.replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'quotes';
  return `${base}.${ext}`;
}

const Spinner: React.FC<{ color: string }> = ({ color }) => (
  <span
    aria-hidden
    style={{
      display: 'inline-block',
      width: 18,
      height: 18,
      border: `2px solid ${color}33`,
      borderTopColor: color,
      borderRadius: '50%',
      animation: 'qe-spin 0.8s linear infinite',
    }}
  />
);

export const ResultsPanel: React.FC<ResultsPanelProps> = ({
  session,
  onToggleStar,
  onSetEdited,
  onResetEdit,
  onRetry,
  title,
}) => {
  const { theme } = useTheme();
  const [toast, setToast] = useState<string | null>(null);

  const starred = useMemo(
    () => session.quotes.filter((q) => q.starred),
    [session.quotes],
  );
  const hasStarred = starred.length > 0;

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2200);
  };

  const handleCopy = async () => {
    const ok = await copyToClipboard(formatQuotesForClipboard(starred, title));
    showToast(ok ? `Copied ${starred.length} quote${starred.length === 1 ? '' : 's'} to clipboard.` : 'Copy failed.');
  };

  const handleExportTxt = () => {
    downloadAsFile(quotesToTxt(starred, title), safeFilename(title, 'txt'), 'text/plain');
    showToast(`Downloaded ${starred.length} as .txt.`);
  };

  const handleExportCsv = () => {
    downloadAsFile(quotesToCsv(starred, title), safeFilename(title, 'csv'), 'text/csv');
    showToast(`Downloaded ${starred.length} as .csv.`);
  };

  if (session.status === 'idle') {
    return (
      <div
        style={{
          padding: `${spacing.xl} ${spacing.lg}`,
          background: theme.colors.background.paper,
          border: `1px dashed ${theme.colors.border.default}`,
          borderRadius: '14px',
          textAlign: 'center',
          color: theme.colors.text.secondary,
          fontSize: '0.95rem',
          fontFamily: fontFamily.sans,
        }}
      >
        Submit content above to see ranked quotes.
      </div>
    );
  }

  if (session.status === 'uploading' || session.status === 'processing') {
    return (
      <div
        style={{
          padding: `${spacing['2xl'] ?? spacing.xl} ${spacing.lg}`,
          background: '#FFFFFF',
          border: `1px solid ${theme.colors.primary[100]}`,
          borderRadius: '14px',
          textAlign: 'center',
          fontFamily: fontFamily.sans,
        }}
      >
        <Stack gap="md" style={{ alignItems: 'center' }}>
          <Spinner color={theme.colors.primary[500]} />
          <div style={{ fontWeight: 600, color: theme.colors.text.primary }}>
            {session.status === 'uploading' ? 'Uploading…' : 'Analyzing content…'}
          </div>
          <div style={{ fontSize: '0.85rem', color: theme.colors.text.secondary }}>
            This usually takes 5–15 seconds.
          </div>
        </Stack>
        <style>{`@keyframes qe-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (session.status === 'error') {
    return (
      <Card variant="outlined" style={{ borderRadius: '14px', borderColor: theme.colors.semantic.error.light }}>
        <Card.Body style={{ padding: spacing.lg }}>
          <Stack gap="sm">
            <p
              style={{
                margin: 0,
                color: theme.colors.semantic.error.default,
                fontWeight: 600,
              }}
            >
              {session.error ?? 'Extraction failed.'}
            </p>
            <Flex gap="sm">
              <Button variant="solid" color="primary" size="sm" onClick={onRetry}>
                Retry
              </Button>
            </Flex>
          </Stack>
        </Card.Body>
      </Card>
    );
  }

  if (session.quotes.length === 0) {
    return (
      <Card variant="outlined" style={{ borderRadius: '14px' }}>
        <Card.Body style={{ padding: spacing.lg }}>
          <Stack gap="sm">
            <p style={{ margin: 0, fontWeight: 600 }}>No quotes surfaced.</p>
            <p style={{ margin: 0, fontSize: '0.9rem', color: theme.colors.text.secondary }}>
              {session.noQuotesReason ??
                'The document did not contain enough quotable prose. Try a different source.'}
            </p>
          </Stack>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Stack gap="md">
      {/* Toolbar */}
      <div
        style={{
          position: 'sticky',
          top: 64,
          zIndex: 20,
          background: 'rgba(255,255,255,0.92)',
          backdropFilter: 'saturate(180%) blur(10px)',
          WebkitBackdropFilter: 'saturate(180%) blur(10px)',
          border: `1px solid ${theme.colors.primary[100]}`,
          borderRadius: '12px',
          padding: `${spacing.sm} ${spacing.md}`,
          fontFamily: fontFamily.sans,
        }}
      >
        <Flex justify="between" align="center" wrap="wrap" gap="sm">
          <Flex align="center" gap="sm">
            <span style={{ fontSize: '0.95rem', fontWeight: 600, color: theme.colors.text.primary }}>
              {session.quotes.length} ranked quote{session.quotes.length === 1 ? '' : 's'}
            </span>
            {hasStarred ? (
              <span
                style={{
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  color: theme.colors.primary[700],
                  background: theme.colors.primary[50],
                  padding: '3px 9px',
                  borderRadius: '999px',
                }}
              >
                {starred.length} starred
              </span>
            ) : (
              <span style={{ fontSize: '0.8rem', color: theme.colors.text.secondary }}>
                Star quotes to copy or export
              </span>
            )}
          </Flex>
          <Flex gap="xs">
            <Button
              variant="solid"
              color="primary"
              size="sm"
              onClick={handleCopy}
              disabled={!hasStarred}
            >
              Copy
            </Button>
            <Button
              variant="outline"
              color="primary"
              size="sm"
              onClick={handleExportTxt}
              disabled={!hasStarred}
            >
              .txt
            </Button>
            <Button
              variant="outline"
              color="primary"
              size="sm"
              onClick={handleExportCsv}
              disabled={!hasStarred}
            >
              .csv
            </Button>
          </Flex>
        </Flex>
      </div>

      {/* Toast */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: spacing.xl,
            left: '50%',
            transform: 'translateX(-50%)',
            padding: `${spacing.sm} ${spacing.lg}`,
            background: theme.colors.text.primary,
            color: '#FFFFFF',
            borderRadius: '999px',
            fontSize: '0.875rem',
            fontWeight: 500,
            fontFamily: fontFamily.sans,
            zIndex: 1000,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}
        >
          {toast}
        </div>
      )}

      {/* Cards */}
      {session.quotes.map((q, idx) => (
        <QuoteCard
          key={q.id}
          quote={q}
          rank={idx + 1}
          onToggleStar={onToggleStar}
          onSetEdited={onSetEdited}
          onResetEdit={onResetEdit}
        />
      ))}
    </Stack>
  );
};
